import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TopBanner from './TopBanner';
import Card from './Card';
import { connect } from 'react-redux';
import './style.css';

// Custom pagination algorithm
function getPagination(totalPages, currentPage, maxVisible = 2) {
	const pages = [];

	// If total pages is small, show all
	if (totalPages <= maxVisible + 2) {
		for (let i = 1; i <= totalPages; i++) {
			pages.push(i);
		}
		return pages;
	}

	// Always include the first page
	const firstPage = 1;
	const lastPage = totalPages;

	// Range for dynamic pages
	const half = Math.floor(maxVisible / 2);
	let start = Math.max(2, currentPage - half);
	let end = Math.min(totalPages - 1, currentPage + half);

	// Ensure we always show maxVisible pages in the middle
	if (end - start + 1 < maxVisible) {
		if (start === 2) {
			end = Math.min(totalPages - 1, start + maxVisible - 1);
		} else if (end === totalPages - 1) {
			start = Math.max(2, end - maxVisible + 1);
		}
	}

	// Add first page
	pages.push(firstPage);

	// Add left ellipsis if needed
	if (start > 2) {
		pages.push("...");
	}

	// Add middle pages
	for (let i = start; i <= end; i++) {
		pages.push(i);
	}

	// Add right ellipsis if needed
	if (end < totalPages - 1) {
		pages.push("...");
	}

	// Add last page
	pages.push(lastPage);

	return pages;
}

function ArticleList({ 
	defaultCategory = '', 
	showBanner = true, 
	onCategoryChange = null,
	hideWrapper = false ,
	user
}) {

	const [articles, setArticles] = useState([]);
	const [featuredArticles, setFeaturedArticles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [query, setQuery] = useState('');
	const [category, setCategory] = useState(defaultCategory);
	const [page, setPage] = useState(1);
	const [pagination, setPagination] = useState({
		total: 0,
		pages: 0,
		limit: 6
	});

	// Update category when defaultCategory prop changes
	useEffect(() => {
		setCategory(defaultCategory);
		setPage(1); // Reset to first page when category changes
	}, [defaultCategory]);

	useEffect(() => {
		fetchArticles();
		// Only fetch featured articles on first page with no search/filter
		if (page === 1 && !query && !category) {
			fetchFeaturedArticles();
		}
	}, [page, query, category]);

	const fetchFeaturedArticles = async () => {
		try {
			const response = await axios.get('/public/articles', {
				params: {
					page: 1,
					limit: 1,
					featured: true
				}
			});

			if (response.data.status) {
				setFeaturedArticles(response.data.data.articles);
			}
		} catch (error) {
			console.error('Error fetching featured articles:', error);
		}
	};


	const fetchArticles = async () => {
		setLoading(true);
		try {
			const params = {
				page,
				limit: 6,
				search: query,
				category
				// Removed article_type filter to include both articles and breaking news
			};

			const response = await axios.get('/public/articles', { params });

			if (response.data.status) {
				setArticles(response.data.data.articles);
				setPagination(response.data.data.pagination);
			}
		} catch (error) {
			console.error('Error fetching articles:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearchChange = (value) => {
		setQuery(value);
		setPage(1);
	};

	const handleCategoryChange = (value) => {
		// Always update local state
		setCategory(value);
		setPage(1);
		
		// If onCategoryChange callback is provided, call it
		if (onCategoryChange) {
			onCategoryChange(value);
		}
	};

	const goToPage = (p) => {
		setPage(Math.min(Math.max(1, p), pagination.pages));
	};

	const content = (
		<>
			<div className='al-page'>
				<div className='al-controls'>
					<div className='al-search'>
						<input
							type='text'
							className='al-search__input'
							placeholder='Search articles'
							value={query}
							onChange={(e) => handleSearchChange(e.target.value)}
						/>
					</div>
					<select 
						className='al-sportSelect' 
						value={category}
						onChange={(e) => handleCategoryChange(e.target.value)}
						aria-label='Select sport'
					>
						<option value=''>All Sports</option>
						<option value='NFL'>NFL</option>
						<option value='CFB'>CFB</option>
						<option value='NBA'>NBA</option>
						<option value='EPL'>EPL</option>
						<option value='La Liga'>La Liga</option>
					</select>
				</div>

				{/* All Articles Section */}
				<h2 className='al-section-title'>All Articles</h2>

				{loading ? (
					<div id='fullHeight'>
						<div id='loading' />
					</div>
				) : (
					<div className='al-grid'>
						{articles.length > 0 ? (
								articles.map((article) => (
									<Card 
										key={article.id} 
										id={article.id}
										slug={article.slug}
										image={article.thumbnail} 
										title={article.title} 
										excerpt={article.meta_desc || 'Read more about this article...'} 
										user={user}
										article_type={article.article_type}
									/>
								))
						) : (
							<div className="al-no-results">
								<p>No articles found{query || category ? ' matching your filters' : ''}.</p>
								{(query || category) && (
									<button 
										className="al-button" 
										onClick={() => {
											setQuery('');
											setCategory('');
											setPage(1);
										}}
									>
										Clear Filters
									</button>
								)}
							</div>
						)}
					</div>
				)}
				{!loading && pagination.pages > 1 && (
					<div className='al-pagination'>
						<button 
							className='al-button al-button--ghost' 
							onClick={() => goToPage(page - 1)} 
							disabled={page === 1}
							aria-label="Previous page"
						>
							&lt;
						</button>
						
						{getPagination(pagination.pages, page, 2).map((pageNumber, index) => {
							if (pageNumber === '...') {
								return (
									<button key={`ellipsis-${index}`} className="al-pagebtn al-ellipsis" disabled>
										...
									</button>
								);
							}
							
							return (
								<button 
									key={pageNumber} 
									className={`al-pagebtn ${page === pageNumber ? 'is-active' : ''}`} 
									onClick={() => goToPage(pageNumber)}
								>
									{pageNumber}
								</button>
							);
						})}
						
						<button 
							className='al-button al-button--ghost' 
							onClick={() => goToPage(page + 1)} 
							disabled={page === pagination.pages}
							aria-label="Next page"
						>
							&gt;
						</button>
					</div>
				)}
			</div>
		</>
	);

	if (hideWrapper) {
		return content;
	}

	return (
		<div className="article-page">
			{showBanner && <TopBanner featuredArticle={featuredArticles[0]} user={user} />}
			{content}
		</div>
	);
}
const mapStateToProps = (state) => ({
	user: state.user
});

const mapDispatchToProps = (dispatch) => ({
	dispatch
});

export default connect(mapStateToProps, mapDispatchToProps)(ArticleList);


